import { useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { TrackedResource } from '@app-types/blackroom-app';
import {
  uploadingFilesVar,
  UploadBatch,
  flattenBatchResources,
} from '../../store/uploadingFiles';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

export interface UploadStatistics {
  uploadingFilesCount: number;
  uploadedFilesCount: number;
  totalFileSize: number;
  remainingFileSize: number;
  remainingTime: number | undefined;
  currentUploadSpeed: number | undefined;
  createResourcesIsDone: boolean;
}

export interface UseUploadProgressResult {
  /** Top-level resources shown in the UI */
  resourceUploads: TrackedResource[];
  /** Upload statistics */
  statistics: UploadStatistics;
  /** Upload progress percentage (0-100) */
  uploadProgression: number;
  /** Total number of top-level items */
  totalItems: number;
  /** True if currently uploading to S3 */
  isUploadingToS3: boolean;
  /** True if S3 upload done but waiting for server confirmation */
  isSavingToServer: boolean;
  /** True if any upload activity is in progress */
  isUploading: boolean;
  /** True if all uploads are complete */
  isComplete: boolean;
}

/* -------------------------------------------------------------------------- */
/*                                  Utilities                                 */
/* -------------------------------------------------------------------------- */

function computeUploadingFilesStatistics(
  allResources: TrackedResource[],
  isCreateResourcesDone: boolean[],
): UploadStatistics {
  let uploadingFilesCount = 0;
  let uploadedFilesCount = 0;
  let uploadedFilesSize = 0;
  let remainingFileSize = 0;
  let totalFileSize = 0;
  let totalUploadDuration = 0;

  allResources.forEach((resource) => {
    totalFileSize += resource.size;
    if (resource.uploadStatus === 'in-progress') {
      uploadingFilesCount += 1;
      remainingFileSize += resource.size;
    } else if (resource.uploadStatus === 'done') {
      uploadedFilesCount += 1;
      uploadedFilesSize += resource.size;
      if (resource.uploadDuration) {
        totalUploadDuration = Math.max(
          totalUploadDuration,
          resource.uploadDuration,
        );
      }
    }
  });

  const currentUploadSpeed = totalUploadDuration
    ? uploadedFilesSize / totalUploadDuration
    : undefined;

  const remainingTime = currentUploadSpeed
    ? remainingFileSize / currentUploadSpeed
    : undefined;

  return {
    uploadingFilesCount,
    uploadedFilesCount,
    totalFileSize,
    currentUploadSpeed,
    remainingFileSize,
    remainingTime,
    createResourcesIsDone: isCreateResourcesDone.every((e) => !!e),
  };
}

/**
 * Formats remaining time as a relative time string
 */
export function formatRemainingTime(
  language: string,
  remainingTime: number,
): string {
  let unit: 'seconds' | 'minutes' | 'hours';
  let timeByUnit: number;
  if (remainingTime < 60 * 1000) {
    unit = 'seconds';
    timeByUnit = remainingTime / 1000;
  } else if (remainingTime < 3600 * 1000) {
    unit = 'minutes';
    timeByUnit = remainingTime / 1000 / 60;
  } else {
    unit = 'hours';
    timeByUnit = remainingTime / 1000 / 60 / 60;
  }
  const formattedTime = new Intl.RelativeTimeFormat(language, {
    numeric: 'auto',
    style: 'long',
  }).format(Math.round(timeByUnit), unit);
  return formattedTime;
}

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

/**
 * Hook that provides upload progress information
 * Computes statistics, progress percentage, and upload state
 */
export function useUploadProgress(): UseUploadProgressResult {
  const uploadingFiles = useReactiveVar(uploadingFilesVar);

  const allUploadingFiles = useMemo(
    () => flattenBatchResources(uploadingFiles, 'files'),
    [uploadingFiles],
  );

  const resourceUploads = useMemo(
    () => flattenBatchResources(uploadingFiles, 'topLevelResources'),
    [uploadingFiles],
  );

  const isCreateResourcesDoneArray = useMemo(
    () =>
      Object.values(uploadingFiles).map(
        (batch: UploadBatch) => batch.isCreateResourcesDone,
      ),
    [uploadingFiles],
  );

  // Note: allUploadingFiles is used internally for statistics calculation
  const statistics = useMemo(
    () =>
      computeUploadingFilesStatistics(
        allUploadingFiles,
        isCreateResourcesDoneArray,
      ),
    [allUploadingFiles, isCreateResourcesDoneArray],
  );

  const uploadProgression = useMemo(() => {
    const { uploadedFilesCount, uploadingFilesCount, createResourcesIsDone } =
      statistics;
    const total = uploadingFilesCount + uploadedFilesCount;

    // Guard against division by zero (can happen after Cancel All)
    if (total === 0) {
      return 0;
    }

    const progression = (100 * uploadedFilesCount) / total;

    // Cap at 99% until server confirms resource creation
    return progression === 100 && !createResourcesIsDone ? 99 : progression;
  }, [statistics]);

  const totalItems = resourceUploads.length;
  const isUploadingToS3 = statistics.uploadingFilesCount > 0;
  const isSavingToServer =
    !isUploadingToS3 && !statistics.createResourcesIsDone;
  const isUploading = isUploadingToS3 || isSavingToServer;
  const isComplete =
    statistics.createResourcesIsDone && uploadProgression === 100;

  return {
    resourceUploads,
    statistics,
    uploadProgression,
    totalItems,
    isUploadingToS3,
    isSavingToServer,
    isUploading,
    isComplete,
  };
}
